import { PropsWithChildren, useCallback, ReactNode, HTMLProps } from 'react'
import { Button, BUTTON_SIZE, BUTTON_TYPE } from '~/app/components'
import styled from '~/app/components/styled'
import { getYaNative } from '@yanolja-org/ya-native'
import conf from '~/app/conf'
import { isNative } from '~/app/utils/typeguards'
import { Icon_failover_h72 } from '@yanolja-org/ya-com-system-icon'
import { Typography } from '@yanolja-org/ya-com-core'
import { useLoading } from '~/app/components/common/LoadingProvider'

/**
 * Retry component
 * author: soojin.oh
 */

// type definitions
type Props = {
  onRetryClick: () => void
  title?: string
  subTitle?: string | ReactNode
  isDisabled?: boolean
  hasError: boolean
  buttonText?: string
  backButtonText?: string
  onBackClick?: () => void
  /**
   * @default true
   * 페이지에서 section(eg. RefundAmountSummary.tsx) 에 retry 를 사용하는 경우 미노출.
   */
  hasBackButton?: boolean
  hasFailOverButton?: boolean
  homeButtonText?: string
  hasHomeButton?: boolean
  isNotAuthorized?: boolean // 로그인 세션 만료 여부
} & Pick<HTMLProps<HTMLDivElement>, 'style'>

// styled components
const Container = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  position: relative;
  padding-top: 13.2rem;
  justify-content: flex-start;
  align-items: center;
  flex-flow: column;
  background: ${({ theme: { color } }) => color.white};
  min-height: 100vh;
  max-width: ${({ theme }) => theme.unit.PC_WIDTH}px;
  margin: 0 auto;

  svg {
    path {
      fill: ${({ theme }) => theme.color.gray400};
    }
  }
`

const Title = styled.div`
  ${Typography('subtitle3').regular}
  color: ${({ theme }) => theme.color.gray900};
  margin-top: 1.2rem;
  margin-bottom: 0.4rem;
`

const SubTitle = styled.div`
  ${Typography('body1').regular}
  color: ${({ theme }) => theme.color.gray600};
  margin-bottom: 1.5rem;
  text-align: center;
`

const FailOverButton = styled(Button)`
  width: 16rem;
  height: 3.6rem;
`

const ButtonContainer = styled.div`
  display: grid;
  gap: 0.8rem;
`

const defaultComment = '네트워크 연결 상태가 좋지 않아요'
const defaultSubtitle = '네트워크 연결 상태를 확인하고, 다시 시도해 주세요.'
const defaultRetryButtonText = '다시 시도하기'
const defaultBackButtonText = '뒤로 가기'
const defaultHomeButtonText = '홈으로 가기'

// Retry component
const Retry = function ({
  title = defaultComment,
  subTitle,
  style,
  isDisabled,
  onRetryClick,
  hasError,
  children,
  buttonText = defaultRetryButtonText,
  backButtonText = defaultBackButtonText,
  homeButtonText = defaultHomeButtonText,
  onBackClick: onBackClickProps,
  hasFailOverButton = true,
  hasBackButton = false,
  hasHomeButton,
  isNotAuthorized = false,
}: PropsWithChildren<Props>) {
  useLoading(isNotAuthorized)

  const onBackClick = useCallback(() => {
    if (onBackClickProps) {
      onBackClickProps()
      return
    }
    const hasBrowserHistory = window.history.length > 1
    if (hasBrowserHistory) {
      window.history.back()
      return
    }

    const yaNative = getYaNative()
    if (isNative(yaNative)) {
      yaNative.dismiss()
    } else {
      window.location.replace(conf.web.url)
    }
  }, [onBackClickProps])

  const onHomeClick = useCallback(() => {
    const yaNative = getYaNative()
    if (isNative(yaNative)) {
      yaNative.deepLink({ link: conf.app.home.url })
    } else {
      window.location.replace(conf.web.url)
    }
  }, [])

  return (
    <>
      {hasError && !isNotAuthorized ? (
        <Container style={style}>
          <Icon_failover_h72 />
          <Title>{title}</Title>
          <SubTitle>{subTitle ?? defaultSubtitle}</SubTitle>
          <ButtonContainer>
            {hasFailOverButton && (
              <FailOverButton
                type={BUTTON_TYPE.BLUE}
                size={BUTTON_SIZE.M}
                title={buttonText}
                isDisabled={isDisabled}
                onClick={onRetryClick}
              />
            )}
            {hasBackButton && (
              <FailOverButton
                type={BUTTON_TYPE.BASIC}
                size={BUTTON_SIZE.M}
                title={backButtonText}
                isDisabled={isDisabled}
                onClick={onBackClick}
              />
            )}
            {hasHomeButton && (
              <FailOverButton
                type={BUTTON_TYPE.BASIC}
                size={BUTTON_SIZE.M}
                title={homeButtonText}
                isDisabled={isDisabled}
                onClick={onHomeClick}
              />
            )}
          </ButtonContainer>
        </Container>
      ) : (
        children
      )}
    </>
  )
}

export default Retry
